/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Brian Bal, Leonid Khramov
 */
import React, { Component } from 'react'
import ReactDOM from 'react-dom'
import * as localForage from 'localforage'
import { TRCButton, TRCInput } from '../../trace'

import './feedback.css'

const initialState = {
    rating: 0,
    transition: 'empty',

    bad: {
        reason_moment_missed: 0,
        reason_short: 0,
        reason_no_touch: 0,
        reason_wrong_player: 0,
        reason_bad_panning: 0,
        reason_spot_placement: 0
    },
    good: {
        category: 'attacking',
        technique: 0,
        pass: 0,
        off_ball_movement: 0,
        shot: 0,
        cross: 0,
        goal: 0,
        final_pass: 0,
        attacking_heading: 0,
        attacking_set_piece: 0,
        attack_transition: 0,
        touch: 0,
        counter_attack: 0,

        one_on_one_defense: 0,
        defensive_set_piece: 0,
        defensive_position: 0,
        defensive_decisions: 0,
        defensive_heading: 0,
        defense_transition: 0,
        tackle: 0,

        gk_from_hands: 0,
        gk_from_feet: 0,
        gk_crosses: 0,
        gk_saves: 0,
        gk_breakaway: 0
    }
}

let tags = {
    bad: [
        { title: 'Moment was missed', key: 'reason_moment_missed' },
        { title: 'Too short, or bad timing', key: 'reason_short' },
        { title: 'I didn\'t touch the ball', key: 'reason_no_touch' },
        { title: 'Wrong player detected', key: 'reason_wrong_player' },
        { title: 'Camera panning is bad', key: 'reason_bad_panning' },
        { title: 'Bad spotlight placement', key: 'reason_spot_placement' },
    ],
    attacking: [
        { title: 'Pass', key: 'pass' },
        { title: 'Shot', key: 'shot' },
        { title: 'Technique', key: 'technique' },
        { title: 'Off ball movement', key: 'off_ball_movement' },
        { title: 'Cross', key: 'cross' },
        { title: 'Goal', key: 'goal' },
        { title: 'Final pass', key: 'final_pass' },
        { title: 'Heading', key: 'attacking_heading' },
        { title: 'Set piece', key: 'attacking_set_piece' },
        { title: 'Counter attack', key: 'counter_attack' },
        { title: 'Touch', key: 'touch' },
        { title: 'Attacking transition', key: 'attack_transition' },
    ],
    defense: [
        { title: 'Reading the game', key: 'defensive_decisions' },
        { title: 'Set piece', key: 'defensive_set_piece' },
        { title: '1v1', key: 'one_on_one_defense' },
        { title: 'Position', key: 'defensive_position' },
        { title: 'Defensive transition', key: 'defense_transition' },
        { title: 'Heading', key: 'defensive_heading' },
        { title: 'Tackle', key: 'tackle' },
    ],
    goalkeeper: [
        { title: 'Dist. from hands', key: 'gk_from_hands' },
        { title: 'Dist. from feet', key: 'gk_from_feet' },
        { title: 'Breakaways', key: 'gk_breakaway' },
        { title: 'Crosses', key: 'gk_crosses' },
        { title: 'Saves', key: 'gk_saves' },
    ]
}

export default class Feedback extends Component {

    constructor(props) {
        super(props)

        this.state = Object.assign({}, initialState)

        this.setFeedbackId = this.setFeedbackId.bind(this)
        this.getFeedbackId = this.getFeedbackId.bind(this)

        this.onGiveFeedback = this.onGiveFeedback.bind(this)
        this.onGoodClip = this.onGoodClip.bind(this)
        this.onBadClip = this.onBadClip.bind(this)
        this.onSendGood = this.onSendGood.bind(this)
        this.onSendBad = this.onSendBad.bind(this)
        this.onCancel = this.onCancel.bind(this)
        this.onShadePressed = this.onShadePressed.bind(this)

        this.onShortChanged = this.onShortChanged.bind(this)
        this.onNoTouchChanged = this.onNoTouchChanged.bind(this)
        this.onWrongPlayerChanged = this.onWrongPlayerChanged.bind(this)
        this.onBadPanningChanged = this.onBadPanningChanged.bind(this)
        this.onSpotlightPlacementChanged = this.onSpotlightPlacementChanged.bind(this)
        this.onThanks = this.onThanks.bind(this)
        this.markActive = this.markActive.bind(this)
        this.markInactive = this.markInactive.bind(this)

        this.onGoodCategoryAttacking = this.onGoodCategoryAttacking.bind(this)
        this.onGoodCategoryDefense = this.onGoodCategoryDefense.bind(this)
        this.onGoodCategoryGoalKeeper = this.onGoodCategoryGoalKeeper.bind(this)

        this.onGoodOptionChanged = this.onGoodOptionChanged.bind(this)
        this.onBadOptionChanged = this.onBadOptionChanged.bind(this)
    }

    onGoodOptionChanged(e) {
        let key = e.currentTarget.id
        let val = this.state.good[key] === 1 ? 0 : 1

        let update = {}
        update[key] = val

        let good = Object.assign({}, this.state.good, update)
        this.setState({ good: good })
    }

    onGoodCategoryAttacking(e) {
        let good = Object.assign({}, this.state.good, { category: 'attacking' })
        this.setState({ good: good })
    }

    onGoodCategoryDefense(e) {
        let good = Object.assign({}, this.state.good, { category: 'defense' })
        this.setState({ good: good })
    }

    onGoodCategoryGoalKeeper(e) {
        let good = Object.assign({}, this.state.good, { category: 'goalkeeper' })
        this.setState({ good: good })
    }



    //// Bad Feedback Event Handlers

    onBadOptionChanged(e) {
        let key = e.currentTarget.id
        let val = this.state.bad[key] === 1 ? 0 : 1

        let update = {}
        update[key] = val

        let bad = Object.assign({}, this.state.bad, update)
        this.setState({ bad: bad })
    }

    onShortChanged(e) {
        this.setState({ short: this.state.short === 0 ? 1 : 0 })
    }

    onNoTouchChanged(e) {
        this.setState({ noTouch: this.state.noTouch === 0 ? 1 : 0})
    }

    onWrongPlayerChanged(e) {
        this.setState({ wrongPlayer: this.state.wrongPlayer === 0 ? 1 : 0 })
    }

    onBadPanningChanged(e) {
        this.setState({ badPanning: this.state.badPanning === 0 ? 1 : 0 })
    }

    onSpotlightPlacementChanged(e) {
        this.setState({ spotlightPlacement: this.state.spotlightPlacement === 0 ? 1 : 0 })
    }



    //// Good or Bad Screen Event Handlers

    onGiveFeedback(e) {
        if (e) { e.preventDefault() }
    }

    onGoodClip(e) {
        if (e) { e.preventDefault() }

        window.location.hash = window.location.hash.replace(/\/feedback.*$/i, '')
        this.setState(initialState)
        this.markInactive()
        this.showHUD('Thanks')
        this.sendFeedback(1)
    }

    onBadClip(e) {
        if (e) { e.preventDefault() }
        if (e) { e.stopPropagation() }

        this.markActive()
        window.location.hash = window.location.hash.replace(/good-or-bad/i, 'reason')
    }



    //// General Event handlers

    onSendBad(e) {
        if (e) { e.preventDefault() }

        this.sendFeedback(-1)

        this.setState({ transition: 'hide' })
        setTimeout(() => {
            window.location.hash = window.location.hash.replace(/\/feedback.*$/i, '')
            this.setState(initialState)
            this.markInactive()
            this.showHUD('Thanks')
        }, 500)
    }

    onSendGood(e) {
        if (e) { e.preventDefault() }

        this.sendFeedback(1)

        this.setState({ transition: 'hide' })
        setTimeout(() => {
            window.location.hash = window.location.hash.replace(/\/feedback.*$/i, '')
            this.setState(initialState)
            this.markInactive()
            this.showHUD('Thanks')
        }, 500)
    }

    onShadePressed(e) {
        if (e) {
            let cp = []
            if (e.target && e.target.className) {
                cp = e.target.className.split(' ')
            }
            if (cp.includes('Feedback-Liked') || cp.includes('Feedback-Reason')) {
                this.onCancel()
            }
        }
    }

    onCancel(e) {
        if (e) { e.preventDefault() }

        this.setState({ transition: 'hide' })
        setTimeout(() => {
            window.location.hash = window.location.hash.replace(/\/feedback.*$/i, '')
            this.setState(initialState)
            this.markInactive()
        }, 500)
    }

    onThanks(e) {
        if (e) { e.preventDefault() }

        window.location.hash = window.location.hash.replace(/\/feedback.*$/i, '')
        this.setState(initialState)
        this.markInactive()
    }

    markActive(e) {
        let event = new CustomEvent('controls-active', { detail: 'feedback' })
        document.dispatchEvent(event)
    }

    markInactive() {
        let event = new CustomEvent('controls-inactive')
        document.dispatchEvent(event)
    }



    //// Send Feedback Logic

    sendFeedback(good) {
        let gidParts = this.props.game.game_id_hash.split('-')
        let gid = gidParts.length > 1 ? gidParts[1] : 'NA'

        let data = Object.assign({}, this.state.good, this.state.bad, {
            game_id: gid,
            team_id: this.props.game.team_id,
            url: window.location.href,
            event_title: this.props.event.title,
            event_utc: this.props.event.utc_time,
        })

        if (this.props.user) {
            data['from'] = this.props.user.user_id
        }

        if (good != null) {
            data['rating'] = good
        }

        this.getFeedbackId().then(feedbackId => {
            if (feedbackId) {
                data['id'] = feedbackId
            }
            fetch(`https://lapi.traceup.com/tracebot-${process.env.REACT_APP_TRACE_ENV}/tracebot/clips/feedback`, {
                method: 'POST',
                mode: 'cors', // no-cors, cors, *same-origin
                cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
                credentials: 'same-origin', // include, *same-origin, omit
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            })
                .then(response => {
                    return response.json()
                })
                .then(json => {
                    if (json.success) {
                        this.setFeedbackId(json.data.id)
                    }
                })
        })

    }

    setFeedbackId(id)  {
        localForage.setItem(`feedback_id_${this.props.game.game_id}-${this.props.event.utc_time}-${this.props.event.title}`, id)
    }

    getFeedbackId(id) {
        return localForage.getItem(`feedback_id_${this.props.game.game_id}-${this.props.event.utc_time}-${this.props.event.title}`)
            .then(val => {
                return val
            })
            .catch(err => {
                console.log(err)
            })
    }

    showHUD(text, time = 1000) {
        if (this.props.toast) {
            this.props.toast(text)
        }
    }

    componentDidUpdate(prevProps) {
        let step = 'initial'
        if (this.props.extras && this.props.extras.feedback) {
            step = this.props.extras.feedback
        }
        let prevStep = 'initial'
        if (prevProps.extras && prevProps.extras.feedback) {
            prevStep = prevProps.extras.feedback
        }

        if (step === 'like' && prevStep === 'initial' && this.state.transition !== 'show') {
            this.setState({ transition: 'show' })
        }
        else if (step === 'like' && prevStep === 'like' && this.state.transition === 'empty') {
            this.setState({ transition: 'show' })
        }
        else if (step === 'dislike' && prevStep === 'initial' && this.state.transition !== 'show') {
            this.setState({ transition: 'show' })
        }
        else if (step === 'dislike' && prevStep === 'dislike' && this.state.transition === 'empty') {
            this.setState({ transition: 'show' })
        }
    }

    render() {
        let step = 'initial'
        if (this.props.extras && this.props.extras.feedback) {
            step = this.props.extras.feedback
        }

        //// LOCK page from scrolling, or un lock it
        if (! this.props.extras || ! this.props.extras.feedback) {
            let bc = document.body.className
            let bcp = bc.split(' ')
            if (bcp.includes('feedback-locked')) {
                bcp = bcp.filter(b => b !== 'feedback-locked')
                document.body.className = bcp.join(' ')
            }
        } else {
            let bc = document.body.className
            let bcp = bc.split(' ')
            if (!bcp.includes('feedback-locked')) {
                bcp.push('feedback-locked')
                document.body.className = bcp.join(' ')
            }
        }

        if (step === 'good-or-bad') {
            return this.renderGoodOrBad()
        } else if (step === 'dislike') {
            return this.renderReason()
        } else if (step === 'like') {
            return this.renderGood()
        } else if (step === 'thanks') {
            return this.renderThankYou()
        } else {
            return this.renderInitial()
        }
    }


    renderThankYou() {
        return(
            <div
                onMouseEnter={this.markActive}
                onMouseOver={this.markActive}
                onMouseOut={this.markInactive}
                onMouseLeave={this.markInactive}
                className="Feedback-Reason"
                onClick={this.onCancel}>
                <h2>Thank You!</h2>
                <p style={{ color: '#000' }}>Your feedback is very valuable to us and we really appreciate it.</p>
            </div>
        )
    }


    renderReason() {
        let portal = document.getElementById('portal')
        return ReactDOM.createPortal((
            <div
                onClick={this.onShadePressed}
                onMouseEnter={this.markActive}
                onMouseOver={this.markActive}
                onMouseOut={this.markInactive}
                onMouseLeave={this.markInactive}
                className={`Feedback-Reason ${this.state.transition}`}>
                <div className="feedback-content">
                    <div className="feedback-inner-content">
                        <h2>What would you like to report?</h2>
                        <form onSubmit={this.onSendBad}>
                            <div className="tags">
                                { tags.bad.map(tag => {
                                    return (
                                        <div className="tag" key={`reason-tag-${tag.key}`}>
                                            <input
                                                type="checkbox"
                                                id={tag.key}
                                                onChange={this.onBadOptionChanged}
                                                value="1"
                                                checked={this.state.bad[tag.key] === 1} />
                                            <label htmlFor={tag.key}>
                                                {tag.title}
                                            </label>
                                        </div>
                                    )
                                })}
                            </div>

                            <div className="buttons">
                                <TRCInput type="submit" onClick={this.onSendBad} value="Send Feedback" full />
                                <TRCButton secondary onClick={this.onCancel} full>Cancel</TRCButton>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        ), portal)
    }

    renderGood() {
        let portal = document.getElementById('portal')
        let enabled = 1

        return ReactDOM.createPortal((
            <div
                onClick={this.onShadePressed}
                onMouseEnter={this.markActive}
                onMouseOver={this.markActive}
                onMouseOut={this.markInactive}
                onMouseLeave={this.markInactive}
                className={`Feedback-Liked ${this.state.transition}`}>
                <div className="feedback-content">
                    <div className="feedback-inner-content">

                        <h2>Liked!</h2>
                        <p className="sub-title">Please tell us more about this clip.</p>
                        <form onSubmit={this.onSendGood}>
                            <p className="button-group">
                                <input
                                    id="good-category-attack"
                                    type="radio"
                                    name="category"
                                    checked={this.state.good.category === 'attacking'}
                                    onChange={this.onGoodCategoryAttacking}
                                    value="attacking" />
                                <label htmlFor="good-category-attack">
                                    Attack
                                </label>

                                <input
                                    id="good-category-defense"
                                    type="radio"
                                    name="category"
                                    checked={this.state.good.category === 'defense'}
                                    onChange={this.onGoodCategoryDefense}
                                    value="defense" />
                                <label htmlFor="good-category-defense">
                                    Defense
                                </label>

                                <input
                                    id="good-category-gk"
                                    type="radio"
                                    name="category"
                                    checked={this.state.good.category === 'goalkeeper'}
                                    onChange={this.onGoodCategoryGoalKeeper}
                                    value="GK" />
                                <label htmlFor="good-category-gk">
                                    GK
                                </label>
                            </p>
                            <div className="tags">
                                { tags[this.state.good.category].map(tag => {
                                    return (
                                        <div className="tag" key={`reason-tag-${tag.key}`}>
                                            <input
                                                type="checkbox"
                                                id={tag.key}
                                                onChange={this.onGoodOptionChanged}
                                                value="1"
                                                checked={this.state.good[tag.key] === 1} />
                                            <label htmlFor={tag.key}>
                                                {tag.title}
                                            </label>
                                        </div>
                                    )
                                })}
                            </div>

                            <div className="buttons">
                                <TRCInput
                                    type="submit"
                                    onClick={this.onSendGood}
                                    value="Send Feedback"
                                    disabled={enabled===0}
                                    primary
                                    full />
                                <TRCButton secondary onClick={this.onCancel} full>Cancel</TRCButton>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        ), portal)
    }

    renderGoodOrBad() {
        return(
            <div
                onMouseEnter={this.markActive}
                onMouseOver={this.markActive}
                onMouseOut={this.markInactive}
                onMouseLeave={this.markInactive}
                className="Feedback step-select-good-bad"
                onClick={this.onCancel}>
                <div className="select-good-bad">
                    <a className="close-btn" onClick={this.onCancel} href="#cancel"><i className="trc-icon-x" /></a>
                    <a className="clip-good" onClick={this.onGoodClip} href="#give-feedback">I like this clip</a>
                    <a className="clip-bad" onClick={this.onBadClip} href="#give-feedback">I don&apos;t like this clip</a>
                </div>
            </div>
        )
    }

    renderInitial() {
        return null
    }

}
